import { cleanupEmissionConsumptionMarkup, renderTextWithFootnotesReferencesV2 } from '@oneaudi/feature-app-utils';
import { Text } from '@oneaudi/unified-web-alpha-components';
import { useAtom, useAtomValue } from 'jotai';
import { Fragment } from 'react';
import { ConsumptionAndEmissionTextStyle } from '~/app/compositions/content/consumption-and-emission-text/styles/consumption-emission-text-style';
import { useI18n } from '~/app/i18n/i18n';
import { useMarketConfig } from '~/app/market-config';
import { state } from '~/storage';

interface Props {
	containerClass?: string;
	withoutPadding?: boolean;
	manualConsumptionDisclaimers?: string[];
}

export const ConsumptionAndEmissionEngineCard = ({
	containerClass,
	manualConsumptionDisclaimers,
	withoutPadding,
}: Props) => {
	const { config } = useMarketConfig();

	if (!config.showCaETextEngineCard) {
		return <ConsumptionAndEmissionTextStyle className={containerClass} />;
	}
	return (
		<ConsumptionAndEmissionText
			manualConsumptionDisclaimers={manualConsumptionDisclaimers}
			containerClass={containerClass}
			withoutPadding={withoutPadding}
		/>
	);
};

export const ConsumptionAndEmissionSummary = ({
	containerClass,
	manualConsumptionDisclaimers,
	withoutPadding,
}: Props) => {
	const { config } = useMarketConfig();

	if (!config.showCaESummaryText) {
		return <ConsumptionAndEmissionTextStyle className={containerClass} />;
	}
	return (
		<ConsumptionAndEmissionText
			manualConsumptionDisclaimers={manualConsumptionDisclaimers}
			containerClass={containerClass}
			withoutPadding={withoutPadding}
		/>
	);
};

export const ConsumptionAndEmissionText = ({ containerClass, manualConsumptionDisclaimers, withoutPadding }: Props) => {
	const [isLandscape] = useAtom(state.isLandscape);
	const currentView = useAtomValue(state.currentView);
	const [consumptionDisclaimersConfiguredCar] = useAtom(state.consumptionDisclaimersConfiguredCarAtom);
	const [consumptionDisclaimer] = useAtom(state.consumptionDisclaimersAtom);
	const { i18n } = useI18n();

	// Only for small markets we need to use envkv values without ranges. Ranges are not available because they are part of technical data which is not available for small markets. In this case, we will fall back to specific disclaimers which are usually only shown on summary page.
	const _consumptionDisclaimer =
		currentView === 'summary'
			? consumptionDisclaimersConfiguredCar
			: consumptionDisclaimer?.length > 0
				? consumptionDisclaimer
				: consumptionDisclaimersConfiguredCar;

	const finalConsumptionDisclaimers = manualConsumptionDisclaimers ?? _consumptionDisclaimer;

	return (
		<ConsumptionAndEmissionTextStyle className={containerClass} tabIndex={0} $withoutPadding={withoutPadding}>
			<label className={`text-wrapper ${isLandscape ? 'is-landscape' : ''}`}>
				{finalConsumptionDisclaimers &&
					finalConsumptionDisclaimers.length > 0 &&
					finalConsumptionDisclaimers.map((disclaimer, index) => {
						return (
							<Fragment key={disclaimer}>
								<label className="entry-wrapper" key={index}>
									<Text tag="span" variant={{ type: 'copy', order: '3', style: 'normal' }}>
										{renderTextWithFootnotesReferencesV2(
											cleanupEmissionConsumptionMarkup(disclaimer?.toString() ?? ''),
											i18n('ngc.a11y.footnotes.cae'),
										)}
									</Text>
								</label>
								{index < finalConsumptionDisclaimers.length - 1 && (
									<Text tag="span" variant={{ type: 'copy', order: '3', style: 'normal' }}>
										{' | '}
									</Text>
								)}
							</Fragment>
						);
					})}
			</label>
		</ConsumptionAndEmissionTextStyle>
	);
};
